import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import SvgCheck from 'src/components/Svgs/SvgCheck'
import SvgClose from 'src/components/Svgs/SvgClose'
import { ExtendCurrency } from 'src/enums'
import Crncy from 'src/localization/Crncy'
import Trns from 'src/localization/Trns'
import {
  VirtualCardPushTaskListModel,
  VirtualCardPushTaskStatus,
} from 'src/models/virtualCardPushTask.models'
import styled, { css, CSSProp } from 'styled-components'

interface VirtualCardPushTaskTableProps {
  className?: string
  currency: ExtendCurrency
  tasks: VirtualCardPushTaskListModel[]
  isBillPay?: boolean
}

const HeaderCellDiv = styled.div<{
  header: string
}>`
  display: table-cell;
  ${getFontCss(
    12,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.medium,
  )}
  padding: 10px;
  border-bottom: 1px solid ${styleConstants.colors.gray_light};
  ${({ header }): CSSProp => {
    return header === 'status'
      ? css`
          width: 10%;
        `
      : header === 'display-name'
        ? css`
            width: 21%;
            max-width: 21%;
          `
        : header === 'recipient'
          ? css`
              width: 27%;
            `
          : header === 'credit-limit'
            ? css`
                width: 21%;
              `
            : header === 'errors'
              ? css`
                  width: 21%;
                `
              : css``
  }}
`

const BodyCellDiv = styled.div<{
  errors?: boolean
}>`
  vertical-align: middle;
  display: table-cell;
  ${getFontCss(
    12,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )};
  padding: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ errors }): CSSProp => {
    return errors
      ? css`
          white-space: normal;
        `
      : css``
  }}
`

const ErrorSpan = styled.span`
  ${getFontCss(
    12,
    styleConstants.colors.red,
    styleConstants.fontFamilies.medium,
  )};
  &:nth-child(n + 2) {
    margin-top: 4px;
  }
`

const VirtualCardPushTaskTable: React.FC<VirtualCardPushTaskTableProps> = (
  props,
): React.ReactElement => {
  return (
    <div
      className={props.className}
      css={css`
        display: table;
        table-layout: fixed;
        background-color: ${styleConstants.colors.gray_lightest};
        border: 1px solid ${styleConstants.colors.gray_medium};
        border-radius: 6px;
        width: 100%;
      `}
    >
      <div
        css={css`
          display: table-row;
        `}
      >
        <HeaderCellDiv header="status">
          <Trns source={'Status'} />
        </HeaderCellDiv>
        <HeaderCellDiv header="display-name">
          <Trns source={props.isBillPay ? 'Vendor Name' : 'Card Name'} />
        </HeaderCellDiv>
        <HeaderCellDiv header="recipient">
          <Trns source={'Email'} />
        </HeaderCellDiv>
        <HeaderCellDiv header="credit-limit">
          <Trns source={props.isBillPay ? 'Invoice Amount' : 'Credit Limit'} />
        </HeaderCellDiv>
        <HeaderCellDiv header="errors">
          <Trns source={'Errors'} />
        </HeaderCellDiv>
      </div>
      {props.tasks.map((task, i) => (
        <div
          key={i}
          css={css`
            display: table-row;
          `}
        >
          <BodyCellDiv>
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
              `}
            >
              {task.status === VirtualCardPushTaskStatus.Completed && (
                <SvgCheck />
              )}
              {task.status === VirtualCardPushTaskStatus.Failed && (
                <SvgClose stroke={styleConstants.colors.red} />
              )}
            </div>
          </BodyCellDiv>
          <BodyCellDiv>{task.record?.truncatedDisplayName}</BodyCellDiv>
          <BodyCellDiv>{task.record?.recipient}</BodyCellDiv>
          <BodyCellDiv>
            {task.record?.balance !== undefined && (
              <Crncy
                currency={task.record?.currency}
                amount={task.record?.balance}
              />
            )}
          </BodyCellDiv>
          <BodyCellDiv errors>
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              {task.errors?.body.error !== undefined && (
                <ErrorSpan>{task.errors.body.error}</ErrorSpan>
              )}
              {task.errors?.body.details?.map((error, j) => (
                <ErrorSpan key={j}>{error.error}</ErrorSpan>
              ))}
            </div>
          </BodyCellDiv>
        </div>
      ))}
    </div>
  )
}

export default VirtualCardPushTaskTable
